import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, BarChart3, TrendingUp, Users, School, Shield, FileText, Eye, GitBranch, Award, DollarSign, AlertCircle, CheckCircle, Target, Zap, MessageSquare, Layers, Lock } from 'lucide-react';
import HeaderEN from '../components/HeaderEN';
import '../styles/index.css';

const PageDirectionsEN = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Boards | Governance"; }, []);

  return (
    <div className="home-container">
      <HeaderEN currentPage="directions" lang="en" />

      {/* Hero Directions */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Building2 size={16} />
            <span>Management & School Boards Portal</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-directions)' }}>Boards</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            Supervise <strong style={{ color: 'var(--actor-directions)' }}>all your schools</strong><br />
            Consolidated view • Strengthened governance
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">∞</div>
              <div className="stat-label">Schools supervised</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">1</div>
              <div className="stat-label">Consolidated view</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Compliance assured</div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto — Why EcoLunch for Boards */}
      <section className="section" data-testid="manifeste-directions">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why EcoLunch for School Boards?</h2>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-gouvernance">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#8B5CF6'}}><Layers size={30} /></div>
              <h3>Centralized governance and total visibility</h3>
            </div>
            <p>School boards and management teams often oversee multiple schools using fragmented tools. EcoLunch provides a <strong>unified governance portal</strong> that consolidates all participation, satisfaction, and nutritional compliance data in real time.</p>
            <p>A single dashboard to manage your entire network, compare performance across schools, and <strong>make informed strategic decisions</strong> instead of navigating scattered Excel files.</p>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-souverainete">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#06B6D4'}}><Lock size={30} /></div>
              <h3>Data sovereignty and guaranteed compliance</h3>
            </div>
            <p>All your school network data is hosted in Quebec and Canada by <strong>OVH Cloud</strong> (Montreal & Toronto). No data is transferred abroad.</p>
            <p>Nutritional standards can be defined at the network level and automatically verified for each school, ensuring <strong>full and traceable compliance</strong> during internal or external audits.</p>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="section problem-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">The Management Challenge</h2>
            <p className="section-subtitle">
              Supervising multiple schools without centralized visibility is a headache
            </p>
          </div>

          <div className="problems-grid">
            <div className="problem-card">
              <div className="problem-icon"><BarChart3 size={32} style={{color: '#84CC16'}} /></div>
              <h3>Fragmented data</h3>
              <p>Each school uses its own tools. Impossible to have a coherent overview.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><MessageSquare size={32} style={{color: '#84CC16'}} /></div>
              <h3>Scattered communication</h3>
              <p>Emails, calls, Excel... Information gets lost between different schools.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><Shield size={32} style={{color: '#84CC16'}} /></div>
              <h3>Uncertain compliance</h3>
              <p>Difficult to ensure all schools meet the same nutritional standards.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><DollarSign size={32} style={{color: '#84CC16'}} /></div>
              <h3>Opaque budgets</h3>
              <p>Limited visibility on consolidated food expenses and optimization opportunities.</p>
            </div>
          </div>

          <div className="solution-intro">
            <h3>EcoLunch centralizes everything</h3>
            <p>A single view to effectively manage all your schools</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Multi-School Governance Portal</h2>
            <p className="section-subtitle">
              Supervise, compare, and optimize food service across your entire network
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Eye size={32} /></div>
              <h3>Real-time consolidated view</h3>
              <p>Single dashboard displaying all your schools. Participation, satisfaction, revenue: everything at a glance.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Comparative analysis</h3>
              <p>Compare food performance between schools. Identify best practices and improvement points.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Portfolio reports</h3>
              <p>Automatic consolidated reports: overall participation, average satisfaction, total expenses. Exportable for board meetings.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Compliance & Standards</h3>
              <p>Define nutritional standards at network level. Automatically verify compliance for each school.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><DollarSign size={32} /></div>
              <h3>Budget management</h3>
              <p>Complete visibility on food expenses per school and overall. Optimize contracts with caterers.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><TrendingUp size={32} /></div>
              <h3>Trends & Forecasts</h3>
              <p>Analyze participation and satisfaction trends. History-based forecasts for budget planning.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Actor supervision</h3>
              <p>View all caterers, schools, and volumes. Quickly identify problems before they worsen.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><AlertCircle size={32} /></div>
              <h3>Strategic alerts</h3>
              <p>Notifications for significant participation drops, recurring problems, or detected non-compliance.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><GitBranch size={32} /></div>
              <h3>Top-down communication</h3>
              <p>Broadcast directives and policies to all schools simultaneously. Ensure network alignment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Governance Section */}
      <section className="section roi-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Strengthened governance</h2>
            <p className="section-subtitle">
              Make informed decisions with reliable and consolidated data
            </p>
          </div>

          <div className="roi-grid">
            <div className="roi-card">
              <div className="roi-metric">
                <Target size={40} />
                <h3>Measurable objectives</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Define participation, satisfaction, and nutritional compliance objectives at network level. 
                Track achievement of these objectives in real time for each school.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <Award size={40} />
                <h3>Internal benchmarking</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Identify high-performing schools and those needing support. 
                Share best practices to improve the entire network.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <CheckCircle size={40} />
                <h3>Audit & Traceability</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Complete history of all decisions, communications, and modifications. 
                Perfect traceability for internal or external audits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EcoPulse CC Mini-Card */}
      <section className="section" data-testid="ecopulse-mini-directions">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-mini-card" style={{borderColor: 'rgba(168, 85, 247, 0.4)'}}>
            <div className="ecopulse-mini-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Zap size={28} />
              </div>
              <div>
                <h3>EcoPulse CC™ <span style={{fontSize: '0.85rem', fontWeight: 500, opacity: 0.7}}>for Boards</span></h3>
                <p style={{fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0}}>
                  One sentence is all you need
                </p>
              </div>
            </div>
            <div className="ecopulse-mini-commands">
              <div className="ecopulse-mini-cmd">Global participation all schools this month</div>
              <div className="ecopulse-mini-cmd">Compare costs per meal between caterers</div>
              <div className="ecopulse-mini-cmd">Q2 budget forecasts</div>
              <div className="ecopulse-mini-cmd">Annual report for commission</div>
            </div>
            <button onClick={() => navigate('/en/ecopulse')} className="ecopulse-mini-link">
              Discover all EcoPulse commands <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                The unified platform to modernize school meals in Quebec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/en/caterers')}>For Caterers</a>
                <a onClick={() => navigate('/en/parents')}>For Parents</a>
                <a onClick={() => navigate('/en/schools')}>For Schools</a>
                <a onClick={() => navigate('/en/directions')}>For Boards</a>
                <a onClick={() => navigate('/en/css')}>For CSS</a>
              </div>

              <div className="footer-column">
                <h4>Platform</h4>
                <a onClick={() => navigate('/en/platform')}>Architecture</a>
                <a onClick={() => navigate('/en/coparenting')}>Co-Parenting</a>
                <a onClick={() => navigate('/en/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/en/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageDirectionsEN;
